/**
 * CADERChargesPanel - California DER Charges Configuration
 *
 * Displays detected CA DER charges (Departing Load + Standby) and allows
 * configuration of key parameters like COD year and eligibility overrides.
 *
 * This appears in the tariff/utility configuration section since these
 * charges are paid to the utility as part of the tariff structure.
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    MenuItem,
    Alert,
    Chip,
    Collapse,
    IconButton,
    Tooltip,
    Divider,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Switch,
    FormControlLabel,
} from '@mui/material';
import {
    CheckCircle,
    Cancel,
    Info,
    ExpandMore,
    ExpandLess,
    ElectricBolt,
    Warning,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { detectCADERCharges, getUtilityName, formatDERCurrency, useAuth } from '@vortx/shared-ui';

// Utility colors
const UTILITY_COLORS = {
    PGE: '#0072CE',  // PG&E Blue
    SCE: '#FF6900',  // SCE Orange
    SDGE: '#00A94F', // SDG&E Green
};

/**
 * CADERChargesPanel Component
 *
 * @param {Object} location - { latitude, longitude }
 * @param {Object} scenario - Current scenario configuration
 * @param {Object} caDerConfig - Current CA DER configuration state
 * @param {Function} onConfigChange - Callback when config changes
 * @param {boolean} readOnly - Whether in read-only mode
 */
function CADERChargesPanel({
    location,
    scenario,
    caDerConfig,
    onConfigChange,
    readOnly = false,
}) {
    const { currentUser } = useAuth();
    const [detection, setDetection] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(true);

    // Sync detection state from caDerConfig (provided by ScenarioEditor)
    // ScenarioEditor now runs detection independently of tab visibility
    useEffect(() => {
        if (!location?.latitude || !location?.longitude) {
            setDetection(null);
            return;
        }

        // If caDerConfig already has detection results from ScenarioEditor, use them
        // This avoids duplicate API calls when user visits Utility tab
        if (caDerConfig?.calculated?.effective_utility && caDerConfig?.has_onsite_generation) {
            // Always calculate generator_kw fresh from scenario prop (not cached caDerConfig)
            // This ensures we show current generator capacity even if caDerConfig is stale
            const freshGeneratorKw = scenario?.generator?.capacity_kw || 0;
            console.log('[CADERChargesPanel] Using existing caDerConfig from ScenarioEditor', {
                scenarioGenerator: scenario?.generator,
                freshGeneratorKw,
                cachedGeneratorKw: caDerConfig.generator_kw
            });
            setDetection({
                detected_utility: caDerConfig.detected_utility,
                utility_name: getUtilityName(caDerConfig.detected_utility),
                is_california_iou: true,
                has_onsite_generation: caDerConfig.has_onsite_generation,
                technologies: caDerConfig.technologies,
                total_generation_kw: caDerConfig.total_generation_kw,
                generator_kw: freshGeneratorKw, // Always fresh from scenario prop
                title_17_compliant: caDerConfig.title_17_compliant ?? false, // From detection result
                standby_charges_apply: caDerConfig.calculated.final_standby_apply,
                departing_load_charges_apply: caDerConfig.calculated.final_departing_load_apply,
                standby_suspension_eligible: caDerConfig.calculated.standby_suspension_eligible,
                standby_suspension_ineligible_reason: caDerConfig.calculated.standby_suspension_ineligible_reason,
                nem_version: caDerConfig.calculated.nem_version,
                nem_exemption_applied: false,
            });
            setLoading(false);
            setError(null);
            return;
        }

        // Fallback: Run detection if caDerConfig doesn't have results yet
        // This handles the case where panel renders before ScenarioEditor's detection completes
        const runDetection = async () => {
            setLoading(true);
            setError(null);
            try {
                console.log('[CADERChargesPanel] Running fallback detection (caDerConfig not ready)');
                const result = await detectCADERCharges(scenario, location, currentUser);
                setDetection(result);

                // Only update config if it doesn't already have detection results
                // ScenarioEditor's detection will handle the primary path
                if (result.has_onsite_generation && !caDerConfig?.calculated?.effective_utility) {
                    const newCalculated = {
                        effective_utility: result.detected_utility,
                        standby_suspension_eligible: result.standby_suspension_eligible,
                        standby_suspension_ineligible_reason: result.standby_suspension_ineligible_reason,
                        final_standby_apply: result.standby_charges_apply,
                        final_departing_load_apply: result.departing_load_charges_apply,
                    };

                    // Extract generator capacity from scenario for standby calculation
                    const generatorKw = scenario?.generator?.capacity_kw || 0;

                    onConfigChange?.({
                        detected_utility: result.detected_utility,
                        has_onsite_generation: true,
                        technologies: result.technologies,
                        total_generation_kw: result.total_generation_kw,
                        generator_kw: generatorKw, // Standby charges based on generator only
                        title_17_compliant: result.title_17_compliant, // From detection result
                        config: caDerConfig?.config || {
                            planned_cod_year: result.suggested_cod_year,
                            system_availability: result.estimated_availability,
                            system_capacity_factor: result.estimated_capacity_factor,
                            islanding_capable: true,
                        },
                        calculated: newCalculated,
                        ca_der_overrides: caDerConfig?.ca_der_overrides || {},
                    });
                }
            } catch (err) {
                console.error('CA DER detection error:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        runDetection();
    }, [location?.latitude, location?.longitude, scenario?.solar_kw, scenario?.battery_kw, scenario?.generator?.capacity_kw, caDerConfig?.calculated?.effective_utility]);

    // Not in California or no generation - don't show
    if (!detection?.detected_utility || !detection?.has_onsite_generation) {
        return null;
    }

    // Utility MUST come from tariff - no fallback to geographic detection
    const utility = caDerConfig?.calculated?.effective_utility;

    // If no tariff utility, show warning
    if (!utility) {
        return (
            <Card sx={{ mb: 2, border: '2px solid #ff9800' }}>
                <CardContent>
                    <Alert severity="warning">
                        CA DER charges require a tariff with utility information. Please select a tariff to see charge estimates.
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    const utilityColor = UTILITY_COLORS[utility] || '#666';
    const suspensionEligible = detection.standby_suspension_eligible;

    const handleConfigUpdate = (field, value) => {
        if (readOnly) return;
        const newConfig = {
            ...caDerConfig,
            config: {
                ...caDerConfig?.config,
                [field]: value,
            }
        };
        onConfigChange?.(newConfig);
    };

    return (
        <Card sx={{ mb: 2, border: `2px solid ${alpha(utilityColor, 0.3)}` }}>
            <CardContent sx={{ pb: 1 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ElectricBolt sx={{ color: utilityColor }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            California DER Charges
                        </Typography>
                        <Chip
                            label={getUtilityName(utility)}
                            size="small"
                            sx={{
                                backgroundColor: alpha(utilityColor, 0.1),
                                color: utilityColor,
                                fontWeight: 600
                            }}
                        />
                    </Box>
                    <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                </Box>

                {/* Suspension Status Alert */}
                {suspensionEligible ? (
                    <Alert
                        severity="success"
                        icon={<CheckCircle />}
                        sx={{ mb: 2 }}
                    >
                        <strong>Eligible for D.21-07-011 Standby Charge Suspension</strong>
                        <br />
                        <Typography variant="body2">
                            Your clean energy system qualifies for suspended standby capacity reservation charges.
                        </Typography>
                    </Alert>
                ) : (
                    <Alert
                        severity="warning"
                        icon={<Warning />}
                        sx={{ mb: 2 }}
                    >
                        <strong>Standard Standby Charges Apply</strong>
                        <br />
                        <Typography variant="body2">
                            System does not meet D.21-07-011 eligibility criteria (Title 17 compliance, 95% availability).
                        </Typography>
                    </Alert>
                )}

                <Collapse in={expanded}>
                    {/* Detection Summary */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            DETECTED CONFIGURATION
                        </Typography>
                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{ border: 0, pl: 0, py: 0.5 }}>Utility Territory</TableCell>
                                    <TableCell sx={{ border: 0, py: 0.5 }}>{getUtilityName(utility)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ border: 0, pl: 0, py: 0.5 }}>Technologies</TableCell>
                                    <TableCell sx={{ border: 0, py: 0.5 }}>
                                        {detection.technologies?.map(t => (
                                            <Chip key={t} label={t} size="small" sx={{ mr: 0.5 }} />
                                        ))}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ border: 0, pl: 0, py: 0.5 }}>Generator Capacity</TableCell>
                                    <TableCell sx={{ border: 0, py: 0.5 }}>{(detection.generator_kw || 0)?.toLocaleString()} kW</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ border: 0, pl: 0, py: 0.5 }}>Title 17 Compliant</TableCell>
                                    <TableCell sx={{ border: 0, py: 0.5 }}>
                                        {detection.title_17_compliant ? (
                                            <Chip label="Yes" color="success" size="small" />
                                        ) : (
                                            <Chip label="No" color="error" size="small" />
                                        )}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Configuration Inputs */}
                    {!readOnly && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                CONFIGURATION
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                                <TextField
                                    label="Commercial Operation Date (Year)"
                                    type="number"
                                    size="small"
                                    value={caDerConfig?.config?.planned_cod_year || new Date().getFullYear() + 1}
                                    onChange={(e) => handleConfigUpdate('planned_cod_year', parseInt(e.target.value))}
                                    sx={{ width: 200 }}
                                    helperText="Determines PCIA vintage rate"
                                />

                                <TextField
                                    label="System Availability (%)"
                                    type="number"
                                    size="small"
                                    value={(caDerConfig?.config?.system_availability || 0.95) * 100}
                                    onChange={(e) => handleConfigUpdate('system_availability', parseFloat(e.target.value) / 100)}
                                    sx={{ width: 160 }}
                                    inputProps={{ min: 0, max: 100, step: 1 }}
                                    helperText=">=95% for suspension"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={caDerConfig?.config?.islanding_capable ?? true}
                                            onChange={(e) => handleConfigUpdate('islanding_capable', e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label="Islanding Capable"
                                />
                            </Box>
                        </Box>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* Exemption Overrides */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            EXEMPTION OVERRIDES
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Override automatic exemption detection for special cases
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                            {/* NEM Version Override */}
                            <TextField
                                label="NEM Version"
                                select
                                size="small"
                                value={caDerConfig?.ca_der_overrides?.nem_version || 'auto'}
                                onChange={(e) => {
                                    const value = e.target.value === 'auto' ? undefined : e.target.value;
                                    onConfigChange?.({
                                        ...caDerConfig,
                                        ca_der_overrides: {
                                            ...caDerConfig?.ca_der_overrides,
                                            nem_version: value,
                                        }
                                    });
                                }}
                                sx={{ width: 180 }}
                                helperText="NEM 1.0 = exempt"
                                disabled={readOnly}
                            >
                                <MenuItem value="auto">Auto-detect</MenuItem>
                                <MenuItem value="NEM_1.0">NEM 1.0 (Grandfathered)</MenuItem>
                                <MenuItem value="NEM_2.0">NEM 2.0</MenuItem>
                                <MenuItem value="NEM_3.0">NEM 3.0 / NBT</MenuItem>
                            </TextField>

                            {/* Departing Load Override */}
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={caDerConfig?.ca_der_overrides?.apply_departing_load_charges ?? true}
                                        onChange={(e) => {
                                            onConfigChange?.({
                                                ...caDerConfig,
                                                ca_der_overrides: {
                                                    ...caDerConfig?.ca_der_overrides,
                                                    apply_departing_load_charges: e.target.checked,
                                                }
                                            });
                                        }}
                                        size="small"
                                        disabled={readOnly}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body2">Departing Load</Typography>
                                        <Typography variant="caption" color="text.secondary">PCIA + NBCs</Typography>
                                    </Box>
                                }
                            />

                            {/* Standby Override */}
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={caDerConfig?.ca_der_overrides?.apply_standby_charges ?? true}
                                        onChange={(e) => {
                                            onConfigChange?.({
                                                ...caDerConfig,
                                                ca_der_overrides: {
                                                    ...caDerConfig?.ca_der_overrides,
                                                    apply_standby_charges: e.target.checked,
                                                }
                                            });
                                        }}
                                        size="small"
                                        disabled={readOnly}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body2">Standby Charges</Typography>
                                        <Typography variant="caption" color="text.secondary">Reservation fees</Typography>
                                    </Box>
                                }
                            />
                        </Box>

                        {/* Show active exemptions info */}
                        {detection?.nem_exemption_applied && (
                            <Alert severity="info" sx={{ mt: 1 }} icon={<Info />}>
                                <Typography variant="body2">
                                    <strong>{detection.nem_version}</strong>: {detection.nem_exemption_notes}
                                </Typography>
                            </Alert>
                        )}

                        {caDerConfig?.ca_der_overrides && Object.keys(caDerConfig.ca_der_overrides).length > 0 && (
                            <Alert severity="warning" sx={{ mt: 1 }} icon={<Warning />}>
                                <Typography variant="body2">
                                    Manual overrides are active. Calculations will use your specified settings instead of auto-detected values.
                                </Typography>
                            </Alert>
                        )}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Charges Summary */}
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            APPLICABLE CHARGES
                        </Typography>
                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{ border: 0, pl: 0, fontWeight: 600 }}>Departing Load Charges</TableCell>
                                    <TableCell sx={{ border: 0 }}>
                                        {detection.departing_load_charges_apply ? (
                                            <Typography variant="body2">
                                                PCIA + NBCs apply to self-consumed generation
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Not applicable
                                            </Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ border: 0, pl: 0, fontWeight: 600 }}>Standby Charges</TableCell>
                                    <TableCell sx={{ border: 0 }}>
                                        {detection.standby_charges_apply ? (
                                            suspensionEligible ? (
                                                <Typography variant="body2" color="success.main">
                                                    Suspended under D.21-07-011 ($0/kW-month)
                                                </Typography>
                                            ) : (
                                                <Typography variant="body2">
                                                    Capacity reservation charges apply
                                                </Typography>
                                            )
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Not applicable
                                            </Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Box>

                    {/* Info Footer */}
                    <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            <Info sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                            CA DER charges are non-bypassable utility charges that apply to behind-the-meter generation
                            in California IOU territories. These costs will be calculated during optimization based on
                            your system's operational profile.
                        </Typography>
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
}

export default CADERChargesPanel;
